import { ReactFlowProvider } from '@xyflow/react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { connectLive } from './lib/live';
import { resolveTheme, watchSystemTheme } from './lib/theme';
import { useAppStore } from './store/useAppStore';
import { CopyPrompt } from './components/CopyPrompt';
import { FirstRun } from './components/FirstRun';
import { HireModal } from './components/HireModal';
import { OrgChart } from './components/OrgChart';
import { SearchPalette } from './components/SearchPalette';
import { SidePanel } from './components/SidePanel';
import { ChangesTable } from './components/ChangesTable';
import { PeopleTable } from './components/PeopleTable';
import { TeamBoard } from './components/TeamBoard';
import { Toasts } from './components/Toasts';
import { UpdateNotice } from './components/UpdateNotice';
import { TopBar } from './components/TopBar';
import { Button } from './components/ui';

export default function App() {
  const status = useAppStore((s) => s.status);
  const error = useAppStore((s) => s.error);
  const theme = useAppStore((s) => s.theme);
  const view = useAppStore((s) => s.view);
  const changesTableOpen = useAppStore((s) => s.changesTableOpen);
  const scenarioName = useAppStore((s) => s.scenario?.name);
  const scenario = useAppStore((s) => s.scenario);
  const bootstrap = useAppStore((s) => s.bootstrap);

  useEffect(() => {
    void bootstrap();
    return connectLive();
  }, [bootstrap]);

  // Painted from the resolved theme, and re-painted when the browser's setting
  // changes — for as long as we are the ones following it.
  useEffect(() => {
    const paint = () => {
      document.documentElement.classList.toggle('dark', resolveTheme(theme) === 'dark');
    };
    paint();
    return theme === 'system' ? watchSystemTheme(paint) : undefined;
  }, [theme]);

  // Name the tab after its scenario, so two of them side by side are telling
  // apart from the tab strip alone.
  useEffect(() => {
    document.title = scenarioName ? `${scenarioName} · Org Planning` : 'Tive Org Planning';
  }, [scenarioName]);

  // Keyboard: Esc closes the panel, ] toggles it.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (event.key === ']') useAppStore.getState().togglePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex h-full flex-col">
      <TopBar />

      {/* `relative` bounds the side panel, which overlays this area on a phone
          rather than docking beside the chart. Without it the panel is
          positioned against the viewport and slides under the header. */}
      <div className="relative flex min-h-0 flex-1">
        <main className="relative min-w-0 flex-1">
          {status === 'loading' ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-[var(--color-ink-3)]">
              <Loader2 size={22} className="animate-spin" />
              <p className="text-[13px]">Loading the org…</p>
            </div>
          ) : status === 'error' ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <AlertTriangle size={24} className="text-[var(--color-moved)]" />
              <p className="max-w-[46ch] text-[13px] text-[var(--color-ink-2)]">{error}</p>
              <Button variant="primary" onClick={() => void bootstrap()}>
                Try again
              </Button>
            </div>
          ) : !scenario ? (
            /* No plan open. The chart needs one to layer changes onto, so this
               asks for one rather than inventing it. */
            <FirstRun />
          ) : view === 'teams' ? (
            <TeamBoard />
          ) : view === 'people' ? (
            <PeopleTable />
          ) : (
            <ReactFlowProvider>
              <OrgChart />
            </ReactFlowProvider>
          )}
        </main>

        <SidePanel />
      </div>

      {/* Over everything, because reading the plan is what you are doing while it
          is open — not a step in editing it. */}
      {changesTableOpen && scenario ? <ChangesTable /> : null}

      <HireModal />
      <CopyPrompt />
      <SearchPalette />
      <Toasts />
      <UpdateNotice />
    </div>
  );
}
